import classnames from 'classnames';

// Internal dependencies
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Visibility from '@components/visibility';
import { ImageToContainer } from '@components/image-container-switcher';
import Label from '@components/block-label';
import LinkControls from '@components/link';
import { ToggleControl, PanelBody } from '@wordpress/components';
import { DEFAULT_CSS_CLASS } from '@constants';
import icons from './icons';

const {
  blockEditor: { useBlockProps, useSetting },
  compose: { createHigherOrderComponent },
  i18n: { __ },
} = wp;

// Enable extended attributes on the following blocks
const enableExtendedAttributesOnBlocks = ['core/image'];

/**
 * Inspector Panel - General Tab
 *
 * @param {Object} props
 */
const InspectorGeneral = (props) => {
  const { attributes, setAttributes, context } = props;
  const { blockVisibility, label, enableLink } = attributes;
  const innerLinkDisabled = context['b3rg/disableNestedLink'];

  return (
    <InspectorControlsGeneral>
      <Label {...{ label, setAttributes }} />
      <Visibility {...{ blockVisibility, setAttributes }} />
      {!innerLinkDisabled && (
        <>
          <PanelBody>
            <ToggleControl
              label={__('Enable link options', 'b3rg')}
              checked={enableLink}
              onChange={(enableLink) => {
                setAttributes({ enableLink });
              }}
            />
          </PanelBody>
          {enableLink && <LinkControls {...{ attributes, setAttributes }} />}
        </>
      )}
    </InspectorControlsGeneral>
  );
};

/**
 * Inspector Panel - Appearance Tab
 *
 * @param {Object} props
 */
const InspectorAppearance = (props) => {
  const { attributes, setAttributes } = props;
  const { blockClassNames } = attributes;
  return (
    <InspectorControlsAppearance>
      <ClassDropdown
        {...{
          blockClassNames,
          setAttributes,
          blockClassPrefix: DEFAULT_CSS_CLASS.IMAGE,
          initialOpen: true,
        }}
      />
    </InspectorControlsAppearance>
  );
};

/**
 * Creating a HOC to add extended attributes to inspector controls of the block.
 */
const BlockEditorWithExtendedAttributes = createHigherOrderComponent(
  (BlockEdit) => {
    return (props) => {
      const { name, attributes, clientId } = props;
      const { useExtendedAttributes, blockVisibility, blockClassNames, url } =
        attributes;

      // set block classes and set to blockProps
      const imageBlockClasses = getBlockClassNames(blockClassNames);
      const classes = classnames({
        disable: !blockVisibility,
      });

      const blockProps = useBlockProps({
        className: `${DEFAULT_CSS_CLASS.IMAGE} ${imageBlockClasses} ${classes}`,
      });

      // Do nothing if it's another block than our defined ones.
      if (!enableExtendedAttributesOnBlocks.includes(props.name)) {
        return <BlockEdit {...props} />;
      }

      const toolbarSettings = useSetting(
        `custom.blocks.${name}.toolbarSettings`,
      );

      return useExtendedAttributes ? (
        <>
          {toolbarSettings?.mobileImage && url && (
            <ImageToContainer
              clientId={clientId}
              icon={icons.imageContainerIcon}
            />
          )}
          <InspectorTabs>
            <InspectorGeneral {...props} />
            <InspectorAppearance {...props} />
          </InspectorTabs>
          <div {...blockProps}>
            <BlockEdit {...props} />
          </div>
        </>
      ) : (
        <BlockEdit {...props} />
      );
    };
  },
  'BlockEditorWithExtendedAttributes',
);

export default BlockEditorWithExtendedAttributes;
